import { html, TemplateResult, LitElement } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import globalStyles from './outline-image.global.pcss?inline';
import encapsulatedStyles from './outline-image.inner.pcss?inline';
import { AdoptedStyleSheets } from '../../controllers/adopted-stylesheets';
import { SlotHelper } from '../../controllers/slot-helper';

/**
 * The element name, reused throughout the codebase
 * @type {string}
 */
const componentName = 'outline-image';

/**
 * The Outline Core Image component
 *
 * A reusable LitElement component for displaying images.
 *
 * @element outline-image
 * @extends LitElement
 * @slot - Placeholder for image to be inserted into the component
 */
@customElement(componentName)
export class OutlineImage extends LitElement {
  /**
   * Adopted stylesheets for the component
   *
   * @type {AdoptedStyleSheets}
   */
  adoptedStyleSheets = new AdoptedStyleSheets(this, {
    globalCSS: globalStyles,
    encapsulatedCSS: encapsulatedStyles,
  });

  private _slotHelper = new SlotHelper(this);

  @property({type: Boolean, reflect: true, attribute: 'has-caption'})
  hasCaption: boolean;

  /**
   * Render method to define the component's template
   *
   * @returns {TemplateResult} A lit-html TemplateResult object representing the component's template
   */
  render(): TemplateResult {
    this.hasCaption = this._slotHelper.isNotEmpty('caption');
      return this.figCaptionTemplate();
  }

  figCaptionTemplate() {
    if (this.hasCaption) {
      return html `
        <figure>
          <slot></slot>
          <figcaption>
            <slot name='caption'></slot>
          </figcaption>
        </figure>
      `
    } else return html`<slot></slot>`
  }
}

/**
 * Declare the component in the global HTMLElementTagNameMap
 */
declare global {
  interface HTMLElementTagNameMap {
    [componentName]: OutlineImage;
  }
}
